/**
 * scraperApi.ts
 * Centralized API client for the Legal Scrapper FastAPI backend.
 * All endpoints are typed and route through a single configurable base URL.
 */

import { API_BASE_URL } from "@/config";
import { getAccessToken } from "@/lib/token-store";

// Scrapper service base URL (judicial search + publicaciones filters live here).
// Keep Legal AI base URL (`API_BASE_URL`) for other modules that use /api/v1.
const BASE_URL = import.meta.env.VITE_SCRAPPER_API_URL || "http://localhost:8082";
const FALLBACK_LOCAL_SCRAPPER_URL = "http://localhost:8082";
export const UNIFIED_CASE_EVENTS_ENABLED =
  String(import.meta.env.VITE_ENABLE_UNIFIED_CASE_EVENTS || "false").toLowerCase() === "true";

function getScrapperBaseUrls(): string[] {
  const urls = [BASE_URL];
  if (BASE_URL !== FALLBACK_LOCAL_SCRAPPER_URL) {
    urls.push(FALLBACK_LOCAL_SCRAPPER_URL);
  }
  return Array.from(new Set(urls));
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ScrapperProceso {
  numero_proceso: string;
  ponente?: string;
  clase?: string;
  tipo_proceso?: string;
  vigente?: string;
  asunto?: string;
  sala_conoce?: string;
  radicado?: string;
  link_descarga?: string;
  fecha_ultima_revision?: string;
  analisis_ia?: AIAnalysisResult | string | null;
  metadata?: any;
  ubicacion?: string;
  subclase?: string;
  recurso?: string;
  naturaleza?: string;
  medida_cautelar?: string;
  demandante?: any;
  demandado?: any;
  sujetos?: any[] | null;
  /** When false, proceso is not shown in Vigilancia (record kept in DB). */
  is_watched?: boolean | null;
  watch_status?: string | null;
  origen?: string;
  fuente?: string;
  delegatura?: string;
  nit_sociedad?: string;
  razon_social?: string;
}

export interface Actuacion {
  id?: string;
  proceso_id?: string;
  fecha: string;
  actuacion: string;
  descripcion?: string;
  tipo_ia?: string;
  riesgo_ia?: string;
  accion_ia?: string;
  has_file?: boolean;
  anexo_path?: string;
  source?: "PROCESO" | "PUBLICACION" | string;
  event_type?: string;
  document_url?: string;
  publication_type?: string;
}

export interface RiesgoBreakdown {
  label: string;
  puntos: number;
}

export interface TimelineInsight {
  fecha: string;
  impacto: string;
  detalle: string;
}

export interface TareaEstrategica {
  accion: string;
  plazo: string;
  objetivo: string;
}

export interface Adelanto {
  fecha: string;
  evento: string;
  importancia: string; // "Alta", "Media", "Baja"
}

export interface Partes {
  demandante: string[];
  demandado: string[];
  otros?: string[];
}

export interface TareaDefensiva {
  titulo: string;
  descripcion: string;
  justificacion?: string;
  prioridad: string;
  plazo_dias?: number;
  impacto?: string;
  basado_en?: string;
  accion?: string;    // Legacy fallback
  objetivo?: string;  // Legacy fallback
  plazo?: string;     // Legacy fallback
}

export interface RelojProcesal {
  termino_critico?: string;
  dias_habiles_transcurridos?: number;
  dias_restantes?: number | string;
  alerta_termino?: "URGENTE_ACCION_INMEDIATA" | "EN_OBSERVACION" | "EN_TERMINO" | string;
  recomendacion_reloj?: string;
}

export interface MatrizProbatoriaItemAcreditado {
  hecho: string;
  prueba_soporte?: string;
  beneficia_a?: "cliente" | "contraparte" | string;
  valor_probatorio?: string;
}

export interface MatrizProbatoriaItemDisputa {
  punto_controvertido: string;
  tesis_cliente?: string;
  tesis_contraparte?: string;
  relevancia?: string;
}

export interface MatrizProbatoriaItemCarga {
  tema: string;
  responsable?: string;
  fundamento?: string;
  estado?: "cumplida" | "incumplida" | "pendiente" | string;
}

export interface MatrizProbatoriaItemVacio {
  prueba_faltante: string;
  impacto?: "alto" | "medio" | "critico" | string;
  accion_sugerida?: string;
}

export interface MatrizProbatoria {
  hechos_acreditados?: MatrizProbatoriaItemAcreditado[];
  hechos_en_disputa?: MatrizProbatoriaItemDisputa[];
  carga_de_la_prueba?: MatrizProbatoriaItemCarga[];
  vacios_probatorios_criticos?: MatrizProbatoriaItemVacio[];
}

export interface AIAnalysisResult {
  rama_derecho?: string;
  resumen?: string;       // New format
  resumen_legal?: string; // Legacy format
  riesgo: string | number;
  riesgo_score?: number;
  riesgo_breakdown?: RiesgoBreakdown[];
  timeline_insights?: TimelineInsight[];
  probabilidades_exito: Record<string, string>;
  tareas_estrategicas: TareaDefensiva[];
  partes?: Partes;
  adelantos?: Adelanto[];
  ataques_disponibles?: string[];
  ataques_inmediatos?: string[];
  oportunidades_nulidad?: string[];
  debilidades_acusacion?: string[];
  debilidades_parte_contraria?: string[];
  reloj_procesal?: RelojProcesal;
  matriz_probatoria?: MatrizProbatoria;
  pronostico_fallo_juez?: string;
  posible_fallo_juez?: string;
  recomendacion_general?: string;
}

/** Reads the first non-empty array from analysis using field aliases. */
export function getAnalysisList(
  analysis: Record<string, unknown> | null | undefined,
  ...keys: string[]
): unknown[] {
  if (!analysis) return [];
  for (const key of keys) {
    const value = analysis[key];
    if (Array.isArray(value) && value.length > 0) return value;
  }
  return [];
}

/** Ensures probability values render correctly in progress bars (e.g. "35%"). */
export function formatProbabilidad(prob: string | number | undefined): string {
  const text = String(prob ?? "").trim();
  if (!text) return "0%";
  if (text.endsWith("%")) return text;
  const digits = text.replace(/[^\d.]/g, "");
  return digits ? `${digits}%` : "0%";
}

const RAMA_LABELS: Record<string, string> = {
  penal: "Penal",
  civil: "Civil",
  laboral: "Laboral",
  familia: "Familia",
  administrativo: "Administrativo",
  comercial: "Comercial",
  constitucional: "Constitucional",
  disciplinario: "Disciplinario",
  agrario: "Agrario",
  tributario: "Tributario",
  general: "Multirrama",
};

export function getRamaLabel(rama?: string): string | null {
  if (!rama) return null;
  return RAMA_LABELS[rama.toLowerCase()] ?? rama;
}

export interface JudicialSearchResult {
  radicado: string;
  tipo_proceso: string;
  juzgado: string;
  despacho?: string;
  demandante?: string;
  demandado?: string;
  estado?: string;
  asunto?: string;
  fuente: string;
  fecha_radicado?: string;
  subclase?: string;
  // Rich metadata from SAMAI / CPNU
  ponente?: string;
  clase?: string;
  fecha_demanda?: string;
  marco_legal?: string;
  naturaleza?: string;
  origen?: string;
  recurso?: string;
  medida_cautelar?: string;
  sala_conoce?: string;
  sentencia?: string;
  fecha_sentencia?: string;
  ubicacion_expediente?: string;
  ubicacion?: string;
  contenido_radicacion?: string;
  metadata?: Record<string, any>;
  sujetos?: { tipo: string; nombre: string; identificacion?: string }[] | string;
  url?: string;
  actuaciones_count?: number;
}

export interface ProcesoArchivo {
  id: string;
  proceso_id: string;
  file_name: string;
  file_path: string;
  file_size: number;
  content_type: string;
  created_at: string;
  fecha_actuacion?: string;
  fecha_documento?: string;
  fecha?: string;
  procesos?: {
    numero_proceso: string;
    title: string;
  };
}

export interface ScrapperNotification {
  id?: string;
  numero_proceso?: string;
  message?: string;
  tipo?: string;
  payload?: Record<string, any>;
  created_at?: string;
}

export interface VigilanciaStatusPayload {
  status?: "running" | "success" | "error" | string;
  started_at?: string | null;
  finished_at?: string | null;
  queued_count?: number;
  message?: string;
}

export interface SearchParams {
  query: string;
  type: "nombre" | "radicado" | "juez" | "juzgado";
  sources: string[];
  tipoSujeto?: string;
  tipoPersona?: string;
  city?: string;
  specialty?: string;
  // Hierarchical IDs for Publicaciones Procesales
  depto_id?: string;
  muni_id?: string;
  entidad_id?: string;
  esp_id?: string;
  despacho_id?: string;
  radicado?: string;
  samai_search_mode?: string;
  samai_exact_phrase?: boolean;
  samai_area_mode?: string;
  samai_estado?: string;
  samai_parte?: string;
  samai_corporacion?: string;
}

export interface SamaiFilterOption {
  value?: string;
  label?: string;
  text?: string;
  id?: string;
  samai_id?: string;
  checked?: boolean;
}

export interface SamaiSearchFilters {
  source_url?: string;
  search_modes?: SamaiFilterOption[];
  area_modes?: SamaiFilterOption[];
  state_options?: SamaiFilterOption[];
  party_options?: SamaiFilterOption[];
  corporacion_options?: SamaiFilterOption[];
  seccion_options?: SamaiFilterOption[];
  ponente_options?: SamaiFilterOption[];
  supports_exact_phrase?: boolean;
}

export interface HierarchicalFilter {
  id: string;
  parent_id: string | null;
  name: string;
  type: "department" | "municipality" | "entity" | "specialty" | "office";
  internal_id?: string;
}

export interface PublicacionActuacion {
  radicado?: string | null;
  publication_date?: string | null;
  title?: string;
  detail?: string;
  files: Array<{ url: string; text: string }>;
  office_id: string;
  fuente: "PUBLICACIONES" | "CPNU";
  metadata?: Record<string, any>;
}

export function buildPublicacionesProxyUrl(fileUrl: string): string {
  const qs = new URLSearchParams({ url: fileUrl });
  return `${getScrapperBaseUrls()[0]}/api/publicaciones/files/proxy?${qs.toString()}`;
}

export async function getSignedPublicacionesProxyUrl(fileUrl: string, clientId?: string): Promise<string> {
  const res = await apiFetch<{
    proxy_url: string;
  }>("/api/publicaciones/files/proxy-sign", {
    method: "POST",
    body: JSON.stringify({
      url: fileUrl,
      client_id: clientId || "",
    }),
  });
  return `${getScrapperBaseUrls()[0]}${res.proxy_url}`;
}

// ─── Helper ───────────────────────────────────────────────────────────────────

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const baseUrls = getScrapperBaseUrls();
  let lastError = "Unknown error";
  const token = getAccessToken();
  const authHeaders: Record<string, string> = {};
  if (token) {
    authHeaders["Authorization"] = `Bearer ${token}`;
  }

  for (const base of baseUrls) {
    try {
      const res = await fetch(`${base}${path}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...authHeaders,
          ...options?.headers,
        },
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => "Unknown error");
        let errMsg = errText;
        try {
          const parsed = JSON.parse(errText);
          if (parsed && typeof parsed === "object") {
            if (typeof parsed.detail === "string") {
              errMsg = parsed.detail;
            } else if (Array.isArray(parsed.detail)) {
              errMsg = parsed.detail.map((d: any) => d.msg || JSON.stringify(d)).join(", ");
            } else if (typeof parsed.message === "string") {
              errMsg = parsed.message;
            }
          }
        } catch (_) {}
        lastError = errMsg;
        continue;
      }
      return res.json();
    } catch (e) {
      lastError = String(e);
    }
  }
  throw new Error(lastError);
}

// ─── Procesos ─────────────────────────────────────────────────────────────────

export async function fetchScrapperProcesos(userId?: string): Promise<ScrapperProceso[]> {
  const qs = userId ? `?user_id=${userId}` : "";
  return apiFetch<ScrapperProceso[]>(`/api/procesos${qs}`);
}

export async function fetchScrapperProceso(radicado: string): Promise<ScrapperProceso> {
  return apiFetch<ScrapperProceso>(`/api/procesos/${radicado}`);
}

export async function watchProceso(
  radicado: string,
  fuente: string = "SAMAI",
  metadata?: any,
  userId?: string
): Promise<{ message: string; task_id?: string }> {
  return apiFetch("/watch", {
    method: "POST",
    body: JSON.stringify({ numero_proceso: radicado, fuente, metadata, user_id: userId }),
  });
}

/** Stops vigilancia on the scrapper (sets is_watched=false); does not delete DB row. */
export async function deleteScrapperProceso(radicado: string): Promise<{ status: string; message?: string }> {
  return apiFetch(`/api/procesos/${encodeURIComponent(radicado)}`, { method: "DELETE" });
}

export async function setScrapperProcesoWatch(
  radicado: string,
  watched: boolean
): Promise<{ status: string; message?: string; is_watched?: boolean }> {
  return apiFetch(`/api/procesos/${encodeURIComponent(radicado)}/watch`, {
    method: "PATCH",
    body: JSON.stringify({ watched }),
  });
}

export async function fetchScrapperNotifications(
  radicado: string,
  limit: number = 20
): Promise<ScrapperNotification[]> {
  return apiFetch<ScrapperNotification[]>(
    `/api/notifications?numero_proceso=${encodeURIComponent(radicado)}&limit=${limit}`
  );
}

export async function fetchVigilanciaCronStatus(): Promise<VigilanciaStatusPayload> {
  const res = await apiFetch<{ status: string; data: VigilanciaStatusPayload }>("/api/admin/vigilancia/status");
  return res?.data || {};
}

export async function runVigilanciaNow(): Promise<{ status: string; message?: string; task_id?: string }> {
  return apiFetch("/api/admin/vigilancia/run-now", { method: "POST" });
}

// ─── Timeline ─────────────────────────────────────────────────────────────────

export async function fetchTimeline(radicado: string): Promise<Actuacion[]> {
  return apiFetch<Actuacion[]>(`/api/procesos/${radicado}/timeline`);
}

export async function fetchCaseEvents(radicado: string, limit: number = 500): Promise<Actuacion[]> {
  const res = await apiFetch<{ status: string; total: number; results: Actuacion[] }>(
    `/api/case-events/${encodeURIComponent(radicado)}?limit=${limit}`
  );
  return Array.isArray(res?.results) ? res.results : [];
}

// ─── AI Analysis ──────────────────────────────────────────────────────────────

export async function analyzeProcess(
  radicado: string,
  clientRole?: string
): Promise<{ analysis: AIAnalysisResult | string }> {
  return apiFetch(`/api/procesos/${radicado}/analyze`, { 
    method: "POST",
    headers: {
      "X-Credits-Deducted": "true"
    },
    body: JSON.stringify(clientRole ? { clientRole } : {})
  });
}

// ─── Draft Generator ──────────────────────────────────────────────────────────

export interface DraftResponse {
  draft: string;
  jurisprudencia_foundations?: string;
  references?: any[];
}

export async function generateDraft(
  radicado: string,
  accion: string,
  objetivo: string,
  analysisContext?: AIAnalysisResult | null,
  extraParams?: {
    fecha_actual?: string;
    form_data?: Record<string, any>;
    missing_fields?: string[];
  }
): Promise<DraftResponse> {
  return apiFetch<DraftResponse>(`/api/procesos/${radicado}/draft`, {
    method: "POST",
    body: JSON.stringify({
      accion,
      objetivo,
      analysis_context: analysisContext,
      fecha_actual: extraParams?.fecha_actual || new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' }),
      form_data: extraParams?.form_data,
      missing_fields: extraParams?.missing_fields,
    }),
  });
}

export async function exportDocument(
  radicado: string,
  content: string,
  format: "docx" | "pdf",
  title?: string
): Promise<void> {
  const safeFilename = `${title || "Escrito_Judicial"}_${radicado || "doc"}`.replace(/[\\/:*?"<>|]/g, "_");

  let htmlBody = content || "";

  // If content is raw Markdown (doesn't contain HTML tags), convert Markdown to HTML
  if (!/<[a-z][\s\S]*>/i.test(htmlBody)) {
    const rawText = htmlBody
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

    htmlBody = `<p>` + rawText
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/^#{3}\s+(.+)$/gm, "<h3>$1</h3>")
      .replace(/^#{2}\s+(.+)$/gm, "<h2>$1</h2>")
      .replace(/^#{1}\s+(.+)$/gm, "<h1>$1</h1>")
      .replace(/^---+$/gm, "<hr/>")
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, "<br/>") + `</p>`;
  }

  if (format === "docx") {
    try {
      const { asBlob } = await import("html-docx-js-typescript");
      const { saveAs } = await import("file-saver");

      const docxHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: 'Times New Roman', serif; font-size: 12pt; line-height: 1.5; color: #000000; }
    p { text-align: justify; text-indent: 36pt; margin-bottom: 10pt; }
    h1, h2, h3 { font-weight: bold; text-align: center; text-transform: uppercase; margin-top: 18pt; margin-bottom: 12pt; }
    strong { font-weight: bold; }
  </style>
</head>
<body>
  ${htmlBody}
</body>
</html>`;

      const blob = await asBlob(docxHtml);
      saveAs(blob as Blob, `${safeFilename}.docx`);
      return;
    } catch (docxErr) {
      console.warn("Client-side DOCX export error, falling back to server:", docxErr);
    }
  }

  if (format === "pdf") {
    const printHtml = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <title>${safeFilename}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Crimson+Text:ital,wght@0,400;0,600;0,700;1,400&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    @page {
      size: letter portrait;
      margin: 25mm 22mm 25mm 25mm;
    }

    body {
      font-family: 'Crimson Text', 'Times New Roman', Georgia, serif;
      font-size: 12pt;
      line-height: 1.65;
      color: #0f172a;
      background: #fff;
    }

    /* ── JUDICIAL DOCUMENT WRAPPER ── */
    .doc-wrapper {
      width: 100%;
      max-width: 720px;
      margin: 0 auto;
      padding: 10px 0;
    }

    /* ── OFFICIAL HEADER BLOCK ── */
    .doc-header {
      text-align: center;
      padding-bottom: 10pt;
      border-bottom: 2.5pt solid #0f172a;
      margin-bottom: 18pt;
    }
    .doc-header .institution {
      font-size: 12.5pt;
      font-weight: 700;
      letter-spacing: 1.2px;
      text-transform: uppercase;
      color: #0f172a;
    }
    .doc-header .radicado-label {
      font-size: 9.5pt;
      font-weight: 600;
      letter-spacing: 2px;
      text-transform: uppercase;
      color: #475569;
      margin-top: 4pt;
    }

    /* ── DOCUMENT CONTENT STYLES ── */
    .doc-body {
      padding-top: 4pt;
    }

    .doc-body p {
      text-align: justify;
      text-indent: 24pt;
      margin-bottom: 9pt;
      font-size: 12pt;
      line-height: 1.7;
      color: #0f172a;
      orphans: 3;
      widows: 3;
    }

    .doc-body h1, .doc-body h2, .doc-body h3, .doc-body h4 {
      font-weight: 700;
      color: #0f172a;
      text-transform: uppercase;
      margin-top: 16pt;
      margin-bottom: 8pt;
      page-break-after: avoid;
    }

    .doc-body h1 { font-size: 14pt; text-align: center; }
    .doc-body h2 { font-size: 13pt; border-bottom: 1.5pt solid #0f172a; padding-bottom: 3pt; }
    .doc-body h3 { font-size: 12pt; border-bottom: 1pt solid #cbd5e1; padding-bottom: 2pt; color: #0f172a; }

    .doc-body ol, .doc-body ul {
      margin-left: 28pt;
      margin-bottom: 10pt;
    }

    .doc-body li {
      margin-bottom: 5pt;
      text-align: justify;
      line-height: 1.65;
    }

    .doc-body li p {
      text-indent: 0;
      display: inline;
      margin-bottom: 0;
    }

    .doc-body strong {
      font-weight: 700;
      color: #0f172a;
    }

    .doc-body hr {
      border: none;
      border-top: 1.5pt solid #0f172a;
      margin: 16pt 0;
    }
  </style>
</head>
<body>
  <div class="doc-wrapper">
    <div class="doc-header">
      <div class="institution">Rama Judicial de la República de Colombia</div>
      <div class="radicado-label">Escrito Judicial &mdash; Radicado N.° ${radicado || "S.N."}</div>
    </div>
    <div class="doc-body">
      ${htmlBody}
    </div>
  </div>
  <script>
    window.onload = function() {
      setTimeout(function() {
        window.print();
      }, 500);
    };
  </script>
</body>
</html>`;

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(printHtml);
      printWindow.document.close();
      printWindow.focus();
    } else {
      // Fallback via iframe if popups are blocked
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(printHtml);
        doc.close();
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      }
    }
    return;
  }

  const token = getAccessToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const baseUrls = getScrapperBaseUrls();
  let res: Response | null = null;

  for (const base of baseUrls) {
    try {
      res = await fetch(`${base}/api/procesos/${radicado}/export`, {
        method: "POST",
        headers,
        body: JSON.stringify({ content, format, title }),
      });
      if (res.ok) break;
    } catch (e) {
      console.warn(`Export failed on ${base}:`, e);
    }
  }

  if (!res || !res.ok) throw new Error("Export failed");

  const blob = await res.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${title || "Escrito"}_${radicado}.${format}`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// ─── RAG Chat ─────────────────────────────────────────────────────────────────

export interface Citation {
  index: number;
  source: string;
  snippet: string;
  full_content: string;
  similarity: number;
}

export async function chatWithAnexos(
  radicado: string,
  query: string
): Promise<{ answer: string; sources: number; citations?: Citation[] }> {
  return apiFetch(`/api/chat/${radicado}`, {
    method: "POST",
    body: JSON.stringify({ query }),
  });
}

// ─── Vectorization ────────────────────────────────────────────────────────────

export async function vectorizeProcess(
  radicado: string
): Promise<{ status: string }> {
  return apiFetch(`/api/procesos/${radicado}/vectorize`, { method: "POST" });
}

// ─── Judicial Search ──────────────────────────────────────────────────────────

export async function searchJudicial(
  params: SearchParams
): Promise<{ total: number; results: JudicialSearchResult[] }> {
  const qs = new URLSearchParams();
  qs.set("q", params.query);
  qs.set("type", params.type);
  params.sources.forEach((s) => qs.append("source", s));
  if (params.type === "nombre") {
    if (params.tipoSujeto) qs.set("tipo_sujeto", params.tipoSujeto);
    if (params.tipoPersona) qs.set("tipo_persona", params.tipoPersona);
  }
  if (params.city) qs.set("city", params.city);
  if (params.specialty) qs.set("specialty", params.specialty);
  
  // Publicaciones filters
  if (params.depto_id) qs.set("depto_id", params.depto_id);
  if (params.muni_id) qs.set("muni_id", params.muni_id);
  if (params.entidad_id) qs.set("entidad_id", params.entidad_id);
  if (params.esp_id) qs.set("esp_id", params.esp_id);
  if (params.despacho_id) qs.set("despacho_id", params.despacho_id);
  if (params.radicado) qs.set("radicado", params.radicado);
  if (params.samai_search_mode) qs.set("samai_search_mode", params.samai_search_mode);
  if (params.samai_exact_phrase) qs.set("samai_exact_phrase", "true");
  if (params.samai_area_mode) qs.set("samai_area_mode", params.samai_area_mode);
  if (params.samai_estado) qs.set("samai_estado", params.samai_estado);
  if (params.samai_parte) qs.set("samai_parte", params.samai_parte);
  if (params.samai_corporacion) qs.set("samai_corporacion", params.samai_corporacion);

  return apiFetch(`/api/search?${qs.toString()}`);
}

export async function fetchSamaiSearchFilters(params?: { corporacion?: string; seccion?: string }): Promise<SamaiSearchFilters> {
  const qs = new URLSearchParams();
  if (params?.corporacion) qs.set("corporacion", params.corporacion);
  if (params?.seccion) qs.set("seccion", params.seccion);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const res = await apiFetch<any>(`/api/samai/search-filters${suffix}`);

  const normalizeOptions = (arr?: any[]): SamaiFilterOption[] =>
    (arr || []).map((o: any) => ({
      ...o,
      value: String(o.value ?? o.id ?? ""),
      label: String(o.label || o.text || o.name || o.value || ""),
      text: String(o.text || o.label || o.name || "")
    }));

  if (!res) return {};

  if (res.ok && Array.isArray(res.data)) {
    return {
      corporacion_options: normalizeOptions(res.data)
    };
  }

  if (Array.isArray(res)) {
    return {
      corporacion_options: normalizeOptions(res)
    };
  }

  const rawData = res.data || res;
  if (Array.isArray(rawData)) {
    return { corporacion_options: normalizeOptions(rawData) };
  }

  return {
    ...rawData,
    corporacion_options: normalizeOptions(rawData.corporacion_options || rawData.corporaciones || (Array.isArray(rawData.data) ? rawData.data : undefined)),
    seccion_options: normalizeOptions(rawData.seccion_options || rawData.secciones),
    ponente_options: normalizeOptions(rawData.ponente_options || rawData.ponentes),
    search_modes: normalizeOptions(rawData.search_modes),
    area_modes: normalizeOptions(rawData.area_modes),
    state_options: normalizeOptions(rawData.state_options),
    party_options: normalizeOptions(rawData.party_options),
  };
}

// ─── Filter Data ──────────────────────────────────────────────────────────────

export async function fetchCities(): Promise<string[]> {
  return apiFetch<string[]>("/api/cities");
}

export async function fetchSpecialties(city: string): Promise<string[]> {
  return apiFetch<string[]>(`/api/specialties/${encodeURIComponent(city)}`);
}

// ─── Archivos ───────────────────────────────────────────────────────────────

export async function uploadProcesoFile(radicado: string, file: File): Promise<any> {
  return uploadProcesoFiles(radicado, [file]);
}

export async function uploadProcesoFiles(radicado: string, files: File[]): Promise<any> {
  const formData = new FormData();
  for (const file of files) {
    formData.append("files", file);
  }
  
  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE_URL}/api/procesos/${radicado}/files`, {
    method: "POST",
    headers,
    body: formData,
  });
  
  if (!res.ok) {
    const errText = await res.text().catch(() => "Unknown error");
    throw new Error(`Upload Error ${res.status}: ${errText}`);
  }
  return res.json();
}

export async function getProcesoFiles(radicado: string): Promise<ProcesoArchivo[]> {
  return apiFetch<ProcesoArchivo[]>(`/api/procesos/${radicado}/files`);
}

export async function getAllProcesoFiles(): Promise<ProcesoArchivo[]> {
  return apiFetch<ProcesoArchivo[]>("/api/archivos");
}

export async function deleteProcesoFile(archivoId: string): Promise<any> {
  return apiFetch(`/api/procesos/files/${archivoId}`, { method: "DELETE" });
}

export async function vectorizeProcesoFile(
  radicado: string,
  fileName: string
): Promise<any> {
  return apiFetch<any>(`/api/procesos/${radicado}/files/${encodeURIComponent(fileName)}/vectorize`, {
    method: "POST",
  });
}

export async function linkFileToProceso(userId: string, fileName: string, numeroProceso: string, vectorize: boolean = false): Promise<any> {
  return apiFetch(`/api/archivos/link`, {
    method: "POST",
    body: JSON.stringify({ 
      user_id: userId, 
      file_name: fileName, 
      numero_proceso: numeroProceso,
      vectorize
    }),
  });
}

// ─── Publicaciones Procesales ─────────────────────────────────────────────────

export async function fetchPublicacionesFilters(
  type: HierarchicalFilter["type"],
  parentId?: string,
  extraFilters?: { muni_id?: string; entidad_id?: string; esp_id?: string }
): Promise<HierarchicalFilter[]> {
  const qs = new URLSearchParams({ type });
  if (parentId) qs.set("parent_id", parentId);
  if (extraFilters?.muni_id) qs.set("muni_id", extraFilters.muni_id);
  if (extraFilters?.entidad_id) qs.set("entidad_id", extraFilters.entidad_id);
  if (extraFilters?.esp_id) qs.set("esp_id", extraFilters.esp_id);
  const res = await apiFetch<{ data: HierarchicalFilter[] } | HierarchicalFilter[]>(
    `/api/judicial-structure/filters?${qs.toString()}`
  );
  return Array.isArray(res) ? res : (res.data || []);
}

export async function watchPublicacionesOffice(data: {
  depto_id: string;
  muni_id: string;
  entidad_id: string;
  esp_id: string;
  despacho_id: string;
  office_name: string;
  historical?: boolean;
}): Promise<{ status: string; message: string }> {
  return apiFetch("/api/publicaciones/watch", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function fetchUltimasActuacionesPublicaciones(data: {
  depto_id?: string;
  muni_id?: string;
  entidad_id?: string;
  esp_id?: string;
  despacho_id?: string;
  radicado?: string;
  nombre_procesado?: string;
  cc_no?: string;
  max_results?: number;
  max_pages?: number;
  enrich_files?: boolean;
  historical?: boolean;
}): Promise<{ total: number; results: PublicacionActuacion[] }> {
  return apiFetch("/api/publicaciones/ultimas-actuaciones", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function submitActionFeedback(
  radicado: string,
  payload: {
    rejected_action: any;
    feedback_reason: string;
    related_actuacion?: string;
    related_prueba?: string;
    etapa_procesal?: string;
  }
): Promise<{ status: string; new_action: TareaDefensiva }> {
  return apiFetch<{ status: string; new_action: TareaDefensiva }>(
    `/api/procesos/${radicado}/feedback-action`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

// ─── BULK PROCESS INGESTION API ──────────────────────────────────────────────

export interface BulkIngestionErrorItem {
  fila: number | string;
  radicado: string;
  origen: string;
  error: string;
}

export interface BulkIngestionResponse {
  status: string;
  job_id?: string;
  total_procesados: number;
  total_insertados: number;
  total_omitidos: number;
  detalles_errores: BulkIngestionErrorItem[];
}

export function getScrapperWebSocketUrl(): string {
  const httpUrl = BASE_URL || "http://localhost:8082";
  const wsProto = httpUrl.startsWith("https") ? "wss" : "ws";
  const host = httpUrl.replace(/^https?:\/\//, "");
  return `${wsProto}://${host}/api/ws/notifications`;
}

export async function downloadProcesosBulkTemplate(format: "xlsx" | "csv" = "xlsx", entity: string = "all"): Promise<Blob> {
  const baseUrls = getScrapperBaseUrls();
  const token = getAccessToken();
  const headers: Record<string, string> = {
    "ngrok-skip-browser-warning": "true",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let lastError = "Error al descargar la plantilla de procesos";
  for (const base of baseUrls) {
    try {
      const res = await fetch(`${base}/api/procesos/plantilla-bulk?format=${format}&entity=${encodeURIComponent(entity)}`, {
        method: "GET",
        headers,
      });
      if (res.ok) {
        return await res.blob();
      } else {
        lastError = await res.text();
      }
    } catch (e: any) {
      lastError = e.message || String(e);
    }
  }
  throw new Error(lastError);
}

export async function bulkIngestProcesosFile(file: File): Promise<BulkIngestionResponse> {
  const baseUrls = getScrapperBaseUrls();
  const token = getAccessToken();
  const formData = new FormData();
  formData.append("file", file);

  const headers: Record<string, string> = {
    "ngrok-skip-browser-warning": "true",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let lastError = "Error durante la ingesta masiva de procesos";
  for (const base of baseUrls) {
    try {
      const res = await fetch(`${base}/api/procesos/bulk-ingest`, {
        method: "POST",
        headers,
        body: formData,
      });
      if (res.ok) {
        return await res.json();
      } else {
        const errText = await res.text();
        try {
          const parsed = JSON.parse(errText);
          lastError = parsed.detail || parsed.message || errText;
        } catch {
          lastError = errText;
        }
      }
    } catch (e: any) {
      lastError = e.message || String(e);
    }
  }
  throw new Error(lastError);
}

export async function bulkIngestProcesosJson(procesos: Array<{ numero_proceso: string; fuente?: string } | string>): Promise<BulkIngestionResponse> {
  const baseUrls = getScrapperBaseUrls();
  const token = getAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let lastError = "Error durante la ingesta masiva de procesos";
  for (const base of baseUrls) {
    try {
      const res = await fetch(`${base}/api/procesos/bulk-ingest`, {
        method: "POST",
        headers,
        body: JSON.stringify({ procesos }),
      });
      if (res.ok) {
        return await res.json();
      } else {
        const errText = await res.text();
        try {
          const parsed = JSON.parse(errText);
          lastError = parsed.detail || parsed.message || errText;
        } catch {
          lastError = errText;
        }
      }
    } catch (e: any) {
      lastError = e.message || String(e);
    }
  }
  throw new Error(lastError);
}

export async function verifyBulkProcesos(procesos?: any[], file?: File | null): Promise<{ total: number; procesos: any[] }> {
  const baseUrls = getScrapperBaseUrls();
  const token = getAccessToken();
  const headers: Record<string, string> = {
    "ngrok-skip-browser-warning": "true",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let body: any;
  if (file) {
    body = new FormData();
    body.append("file", file);
  } else {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify({ procesos });
  }

  let lastError = "Error al verificar procesos";
  for (const base of baseUrls) {
    try {
      const res = await fetch(`${base}/api/procesos/verify-bulk`, {
        method: "POST",
        headers,
        body,
      });
      if (res.ok) {
        return await res.json();
      } else {
        const errText = await res.text();
        try {
          const parsed = JSON.parse(errText);
          lastError = parsed.detail || parsed.message || errText;
        } catch {
          lastError = errText;
        }
      }
    } catch (e: any) {
      lastError = e.message || String(e);
    }
  }
  throw new Error(lastError);
}

